import React, { createContext } from "react";
import { RoomCollection } from "@lifeat/shared/data-models/Rooms/RoomEntity";
import { useDbWithLocalCache } from "@hooks/useDbWithLocalCache";

export type RoomSettingsContextType = {
    isVideoCallingDisabled: boolean;
    setVideoCallingDisabled: (disabled: boolean) => Promise<void>;
    isGuestsCameraDisabled: boolean;
    setGuestsCameraDisabled: (disabled: boolean) => Promise<void>;
    isGuestsMicDisabled: boolean;
    setGuestsMicDisabled: (disabled: boolean) => Promise<void>;
    isScreenShareDisabled: boolean;
    setScreenShareDisabled: (disabled: boolean) => Promise<void>;
};

export const RoomSettingsContext: React.Context<RoomSettingsContextType | undefined> = createContext<
    RoomSettingsContextType | undefined
>(undefined);

interface RoomSettingsContextProps {
    readonly roomId: string;
    readonly children?: React.ReactNode;
}
export const RoomSettingsContextProvider = (props: RoomSettingsContextProps) => {
    const { roomId, children } = props;

    const [isVideoCallingDisabled, setVideoCallingDisabled] = useDbWithLocalCache({
        node: RoomCollection.at(roomId).at("videoCallSettings").at("disableRoomVideoCall").node,
        initialValue: false,
    });
    const [isGuestsCameraDisabled, setGuestsCameraDisabled] = useDbWithLocalCache({
        node: RoomCollection.at(roomId).at("videoCallSettings").at("disableCallGuestsCamera").node,
        initialValue: false,
    });
    const [isGuestsMicDisabled, setGuestsMicDisabled] = useDbWithLocalCache({
        node: RoomCollection.at(roomId).at("videoCallSettings").at("disableCallGuestsMic").node,
        initialValue: false,
    });
    const [isScreenShareDisabled, setScreenShareDisabled] = useDbWithLocalCache({
        node: RoomCollection.at(roomId).at("videoCallSettings").at("disableCallScreenShare").node,
        initialValue: false,
    });

    const providerVals = {
        isVideoCallingDisabled,
        setVideoCallingDisabled,
        isGuestsCameraDisabled,
        setGuestsCameraDisabled,
        isGuestsMicDisabled,
        setGuestsMicDisabled,
        isScreenShareDisabled,
        setScreenShareDisabled,
    };

    return <RoomSettingsContext.Provider value={providerVals}>{children}</RoomSettingsContext.Provider>;
};

export const useRoomSettingsContext = () => {
    const context = React.useContext(RoomSettingsContext);
    if (context == null) {
        throw new Error("Missing RoomSettingsContext");
    }
    return context;
};
